import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  CreateTemplateRequest,
  SendMessageRequest,
  SendTemplateRequest,
  StartConversationRequest,
  WhatsappMediaUploadResponse,
  WhatsappMessageResponse,
  WhatsappTemplate,
} from '../models/whatsapp.model';

@Injectable({ providedIn: 'root' })
export class WhatsappMessageApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiUrl}api/whatsapp`;

  /**
   * Envío libre: texto, adjunto, o adjunto con pie. Para adjuntar, subir primero el
   * archivo con `uploadMedia` y pasar el `mediaId` + `mediaType` en el request.
   */
  sendMessage(
    conversationId: number,
    req: SendMessageRequest
  ): Observable<WhatsappMessageResponse> {
    return this.http.post<WhatsappMessageResponse>(
      `${this.base}/conversations/${conversationId}/messages`,
      req
    );
  }

  /**
   * URL del adjunto de un mensaje. El backend lo sirve desde su propia copia en storage
   * porque `mediaPath` es una ruta y no un id de /api/files, así que no hay forma de
   * pedirlo por ahí. Requiere el bearer token, de modo que se descarga con HttpClient
   * (que sí pasa por el interceptor) y no poniéndola en el `src` de un <img>.
   */
  messageMediaUrl(messageId: number): string {
    return `${this.base}/media/message/${messageId}`;
  }

  /** Descarga el adjunto como Blob para poder pintarlo con una object URL. */
  downloadMessageMedia(messageId: number): Observable<Blob> {
    return this.http.get(this.messageMediaUrl(messageId), { responseType: 'blob' });
  }

  /**
   * Sube el archivo a Meta y devuelve su mediaId. Sirve tanto para la cabecera de una
   * plantilla (viaja en `headerMediaId`) como para adjuntarlo a un mensaje libre
   * (viaja en `mediaId`). El id caduca a los 30 días y sólo vale para ese número.
   */
  uploadMedia(
    whatsappPhoneId: number,
    file: File
  ): Observable<WhatsappMediaUploadResponse> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<WhatsappMediaUploadResponse>(
      `${this.base}/media?whatsappPhoneId=${whatsappPhoneId}`,
      form
    );
  }

  /** Plantillas aprobadas de la WABA. Se consultan a Meta en vivo. */
  listTemplates(): Observable<WhatsappTemplate[]> {
    return this.http.get<WhatsappTemplate[]>(`${this.base}/templates`);
  }

  /** Alta en Meta. Sólo admin. Devuelve la plantilla en estado PENDING. */
  createTemplate(req: CreateTemplateRequest): Observable<WhatsappTemplate> {
    return this.http.post<WhatsappTemplate>(`${this.base}/templates`, req);
  }

  /** Borra la plantilla en Meta (todos sus idiomas). Sólo admin. */
  deleteTemplate(name: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/templates/${encodeURIComponent(name)}`);
  }

  /** Escribe primero a un número que nunca contactó. Crea la conversación y el contacto. */
  startConversation(req: StartConversationRequest): Observable<WhatsappMessageResponse> {
    return this.http.post<WhatsappMessageResponse>(
      `${this.base}/conversations/start`,
      req
    );
  }

  /** Único envío posible con la ventana de 24h cerrada. */
  sendTemplate(
    conversationId: number,
    req: SendTemplateRequest
  ): Observable<WhatsappMessageResponse> {
    return this.http.post<WhatsappMessageResponse>(
      `${this.base}/conversations/${conversationId}/messages/template`,
      req
    );
  }
}
